import { nextTick } from 'vue';
import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteMeta,
  type RouteRecord,
  type RouteRecordRaw,
} from 'vue-router';
import BrokerDashboard from '@/views/dashboard/BrokerDashboard.vue';
import store, { Commit } from '@/store';
import CarrierOverviewDashboard from '@/views/CarrierOverviewDashboard.vue';
import Logout from '@/views/Logout.vue';
import {
  AreAllAccountsAllowed,
  AreAllRouteGroupsAllowed,
  GetAllowedAccountIds,
  GetUserRole,
  IsUserABroker,
  IsUserAnAdmin,
  UserHasPermission,
} from '@/utilities/AuthUtils';
import { UserRole } from '@/types/UserRole';
import Unauthorized from '@/views/Unauthorized.vue';
import CarrierList from '@/views/carrier/CarrierList.vue';
import AccountList from '@/views/account/AccountList.vue';
import ManagementAnalyticsDashboard from '@/views/reporting/ManagementAnalyticsDashboard.vue';
import AccountForm from '@/views/account/AccountForm.vue';
import AccountView from '@/views/account/AccountView.vue';
import AccountOverview from '@/views/account/AccountOverview.vue';
import PolicyDetails from '@/views/policy/PolicyDetails.vue';
import AccountCybeta from '@/views/account/cyber/AccountCybeta.vue';
import CyberForm from '@/views/tools/CyberForm.vue';
import AccountBenchmarking from '@/views/account/benchmarking/AccountBenchmarking.vue';
import ParentAccountView from '@/views/parent_account/ParentAccountView.vue';
import ParentAccountOverview from '@/views/parent_account/ParentAccountOverview.vue';
import ParentAccountCardView from '@/views/parent_account/ParentAccountCardView.vue';
import { StoreMutationType } from '@/types/StoreMutationType';
import PolicyList from '@/views/policy/PolicyList.vue';
import DocumentList from '@/views/document/DocumentList.vue';
import DocumentUpload from '@/views/document/v2/DocumentUpload.vue';
import CarrierView from '@/views/carrier/CarrierView.vue';
import CarrierOverview from '@/views/carrier/CarrierOverview.vue';
import CarrierInsurerFinancials from '@/views/carrier/CarrierInsurerFinancials.vue';
import CarrierGroupMapping from '@/views/admin/CarrierGroupMapping.vue';
import CarrierSurveyList from '@/views/carrier/CarrierSurveyList.vue';
import CarrierSurvey from '@/views/carrier/CarrierSurvey.vue';
import PolicyType from '@/views/admin/PolicyType.vue';
import PermissionAdmin from '@/views/admin/PermissionAdmin/PermissionAdmin.vue';
import Reporting from '@/views/admin/Reporting.vue';
import AccountExposure from '@/views/account/AccountExposure.vue';
import AccountClaims from '@/views/account/AccountClaims.vue';
import VolatilityForm from '@/views/tools/VolatilityForm.vue';
import VolatilityShow from '@/views/tools/VolatilityShow.vue';
import RevenueDashboard from '@/views/reporting/RevenueDashboard.vue';
import YieldAnalysis from '@/views/reporting/YieldAnalysis.vue';
import NotFound from '@/components/NotFound.vue';
import AMBestRatings from '@/views/carrier/AMBestRatings.vue';
import WholesalerGroupList from '@/views/wholesaler_group/WholesalerGroupList.vue';
import WholesalerGroupView from '@/views/wholesaler_group/WholesalerGroupView.vue';
import WholesalerGroupOverview from '@/views/wholesaler_group/WholesalerGroupOverview.vue';
import Wholesaler from '@/views/admin/Wholesaler.vue';
import QuotePrograms from '@/views/quote/QuoteProgram.vue';
import { BrokerRequiredPermissions, UserPermission } from '@/types/UserPermissions';
import Quote from '@/views/quote/Quote.vue';
import DataQuality from '@/views/admin/DataQuality.vue';
import type { User } from '@/types/User';
import AccountProgramList from '@/views/account/AccountProgramList.vue';
import AccountProgramDetails from '@/views/account/AccountProgramDetails.vue';
import AccountProgramForm from '@/views/account/AccountProgramForm.vue';
import AccountRenewalView from '@/views/account/AccountRenewalView.vue';
import Home from '@/views/Home.vue';
import CacheManagement from '@/views/admin/CacheManagement.vue';
import TowerV2 from '@/views/tower/v2/Tower.vue';
import AccountAnalytics from '@/views/account/AccountAnalytics.vue';
import ClientView from '@/views/account/ClientView.vue';
import ManageProfile from '@/views/profile/ManageProfile.vue';
import TowerVisualizerPage from '@/views/tower_visualizer/TowerVisualizerPage.vue';
import TowerDashboard from '@/views/tower_dashboard/TowerDashboard.vue';
import TowerVisualizerDashboard from '@/views/tower_visualizer/TowerVisualizerDashboard.vue';
import Playground from '@/components/Playground.vue';
import NotificationPage from '@/views/NotificationPage.vue';
import AccountClientReadiness from '@/views/account/AccountClientReadiness.vue';
import NotificationPreferences from '@/views/NotificationPreferences.vue';
import AnalyticsHub from '@/views/admin/AnalyticsHub.vue';
import AnalyticsList from '@/views/admin/AnalyticsList.vue';
import AnalyticsView from '@/components/AnalyticsView.vue';
import StockDropForm from '@/views/tools/StockDropForm.vue';
import StockDropPage from '@/views/tools/StockDropPage.vue';
import ComponentShowcase from '@/views/admin/CustomComponentShowcase.vue';
import PromptManager from '@/views/document/PromptManager.vue';
import AIVerificationResultsPage from '@/views/document/AIVerificationResultsPage.vue';
import ReportingHub from '@/views/admin/ReportingHub.vue';
import ReportingList from '@/views/admin/ReportingList.vue';
import LandingPage from '@/views/LandingPage.vue';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    allowedUserTypes?: UserRole[];
    requiredPermissions?: UserPermission[] | Partial<Record<UserRole, UserPermission[]>>;
    routeGroup?: string;
    skipRouteGroup?: boolean;
    isParentAccount?: boolean;
    action?: string;
    protectAccount?: boolean;
  }
}

interface RouteMetadata {
  title?: string;
  allowedUserTypes?: UserRole[];
  requiredPermissions?: UserPermission[] | Partial<Record<UserRole, UserPermission[]>>;
  routeGroup?: string;
  skipRouteGroup?: boolean;
  isParentAccount?: boolean;
  action?: string;
  protectAccount?: boolean;
}

type RouteWithMetadata = RouteRecordRaw & { meta?: RouteMeta };

const routes: RouteWithMetadata[] = [
  {
    path: '/dashboard',
    meta: {
      title: 'Dashboard',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
    },
    component: BrokerDashboard,
  },

  {
    path: '/v2/tower',
    meta: {
      title: 'Towers',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
      routeGroup: 'Tower',
    },
    components: { default: TowerDashboard },
  },

  {
    path: '/v2/tower/:id',
    meta: {
      title: 'v2 Tower Creator',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
      routeGroup: 'Tower',
    },
    components: { default: TowerV2 },
  },

  {
    name: 'AccountForm',
    path: '/account/:id/edit',
    meta: {
      title: 'Edit Account',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
      routeGroup: 'Account',
    },
    component: AccountForm,
  },

  {
    path: '/wholesaler-group',
    meta: {
      routeGroup: 'WholesalerGroup',
      title: 'Wholesaler List',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Market],
    },
    component: WholesalerGroupList,
  },

  {
    path: '/wholesaler-group/:id',
    component: WholesalerGroupView,
    children: [
      {
        path: 'overview',
        component: WholesalerGroupOverview,
        meta: {
          routeGroup: 'WholesalerGroup',
          title: 'Wholesaler',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Market],
        },
      },
      {
        path: 'policy-detail',
        name: 'wholesaler-policy-detail',
        component: PolicyList,
        meta: {
          routeGroup: 'WholesalerGroup',
          title: 'Wholesaler',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Market],
        },
        props: { scenario: 'wholesaler' },
      },
    ],
  },

  {
    path: '/wholesaler-group/custom/:id',
    component: WholesalerGroupView,
    children: [
      {
        path: 'overview',
        component: WholesalerGroupOverview,
        meta: {
          routeGroup: 'WholesalerGroupCustom',
          title: 'Wholesaler',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Market],
        },
      },
      {
        path: 'policy-detail',
        name: 'wholesaler-group-policy-detail',
        component: PolicyList,
        meta: {
          routeGroup: 'WholesalerGroupCustom',
          title: 'Wholesaler',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Market],
        },
        props: { scenario: 'custom-wholesaler' },
      },
    ],
  },

  {
    path: '/account/:id',
    component: AccountView,
    meta: {
      protectAccount: true,
      routeGroup: 'Account',
    },
    children: [
      {
        path: 'overview',
        name: 'account-overview',
        component: AccountOverview,
        meta: {
          title: 'Account Overview',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          routeGroup: 'Account',
          protectAccount: true,
          requiredPermissions: { [UserRole.Client]: [UserPermission.OverviewRead] },
        },
      },
      {
        path: 'policy-detail',
        name: 'account-policy-detail',
        component: PolicyDetails,
        meta: {
          title: 'Policies',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          routeGroup: 'Account',
          protectAccount: true,
        },
        props: { scenario: 'account' },
      },
      {
        path: 'benchmarking',
        name: 'account-benchmarking',
        component: AccountBenchmarking,
        meta: {
          title: 'Benchmarking',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin],
        },
      },
      {
        path: 'benchmarking/:selected',
        name: 'account-benchmarking-selected',
        component: AccountBenchmarking,
        props: true,
        meta: {
          title: 'Benchmarking',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin],
        },
      },
      {
        path: 'exposure-data',
        name: 'account-exposure',
        component: AccountExposure,
        meta: {
          title: 'Exposure',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin],
        },
      },
      {
        path: 'claim-detail',
        name: 'account-claim-detail',
        component: AccountClaims,
        meta: {
          title: 'Claims',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          requiredPermissions: [UserPermission.ClaimsRead],
        },
      },
      {
        path: 'towers',
        name: 'account-towers',
        component: TowerVisualizerDashboard,
        meta: {
          title: 'Towers',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          requiredPermissions: { [UserRole.Client]: [UserPermission.TowersRead] },
        },
        props: (route) => ({
          accountId: route.params.id,
          getTowersBy: 'Account',
        }),
      },
      {
        path: 'towers/:towerId',
        component: TowerVisualizerPage,
        meta: {
          allowedUserTypes: [UserRole.Broker, UserRole.Admin],
        },
        props: (route) => ({
          accountId: route.params.id,
          towerId: route.params.towerId,
        }),
      },
      {
        path: 'towers/new',
        component: TowerVisualizerPage,
        meta: {
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          requiredPermissions: { [UserRole.Client]: [UserPermission.TowersRead] },
        },
        props: (route) => ({
          accountId: route.params.id,
          serializedTower: route.query.serializedTower,
        }),
      },
      {
        path: 'programs',
        component: AccountProgramList,
        meta: {
          title: 'Programs',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          routeGroup: 'Account',
          protectAccount: true,
        },
      },
      {
        path: 'programs/new',
        component: AccountProgramForm,
        meta: {
          title: 'Create Program',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin],
          action: 'create-new-program',
          protectAccount: true,
        },
      },
      {
        path: 'programs/:programId',
        name: 'account-program-details',
        component: AccountProgramDetails,
        meta: {
          title: 'Program Details',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          routeGroup: 'Account',
          protectAccount: true,
        },
        children: [
          {
            path: 'towers',
            component: TowerVisualizerPage,
            meta: {
              allowedUserTypes: [UserRole.Broker, UserRole.Admin],
            },
            props: (route) => ({
              accountId: route.params.id,
              programId: route.params.programId,
              getTowersBy: 'Account & Program',
            }),
          },
        ],
      },
      {
        path: 'programs/:programId/edit',
        component: AccountProgramForm,
        meta: {
          title: 'Edit Program',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin],
        },
        props: { isEditMode: true },
      },
      {
        path: 'programs/:programId/tower',
        component: AccountProgramForm,
        meta: {
          title: 'Edit Program',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin],
        },
        props: { isEditMode: true },
      },
      {
        path: 'quotes',
        name: 'account-quotes',
        component: QuotePrograms,
        meta: {
          title: 'Quotes',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
        },
      },
      {
        path: 'quotes/program/:programId',
        name: 'account-quote-program',
        component: Quote,
        meta: {
          title: 'Quote',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
        },
      },
      {
        path: 'renewal',
        name: 'account-renewal',
        component: AccountRenewalView,
        meta: {
          title: 'Renewal',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          routeGroup: 'Account',
          protectAccount: true,
          requiredPermissions: { [UserRole.Client]: [UserPermission.RenewalRead] },
        },
      },
      {
        path: 'client-readiness',
        name: 'account-client-readiness',
        component: AccountClientReadiness,
        meta: {
          title: 'Client Readiness',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin],
        },
      },
      {
        path: 'documents',
        component: DocumentList,
        meta: {
          title: 'Documents',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          routeGroup: 'Account',
          protectAccount: true,
        },
      },
      {
        path: 'documents/upload',
        component: DocumentUpload,
        meta: {
          title: 'Upload Document',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
        },
      },
      {
        path: 'analytics',
        name: 'account-analytics',
        component: AccountAnalytics,
        meta: {
          title: 'Analytics',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
        },
        children: [
          {
            path: 'category/:categoryId',
            name: 'account-analytics-category',
            component: AnalyticsList,
            props: (route) => ({
              accountId: route.params.id,
              categoryId: route.params.categoryId,
            }),
            children: [
              {
                path: 'dashboard/:dashboardId',
                name: 'account-analytics-category-dashboard',
                component: AnalyticsView,
                props: (route) => ({
                  dashboardId: route.params.dashboardId,
                }),
              },
              {
                path: 'volatility/:volatilityId',
                meta: {
                  allowedUserTypes: [UserRole.Admin, UserRole.Broker, UserRole.Client],
                  categoryType: 'Other',
                },
                name: 'account-analytics-category-volatility',
                component: VolatilityShow,
                props: true,
              },
              {
                path: 'volatility/new',
                meta: {
                  Title: 'New Volatility Model',
                  allowedUserTypes: [UserRole.Admin, UserRole.Broker, UserRole.Client],
                },
                name: 'account-analytics-category-volatility-new',
                component: VolatilityForm,
                props: (route) => ({
                  accountId: route.params.id,
                }),
              },
              {
                path: 'volatility/:volatilityId/edit',
                meta: {
                  title: 'Edit Volatility Model',
                  allowedUserTypes: [UserRole.Admin, UserRole.Broker],
                },
                name: 'account-analytics-category-volatility-edit',
                component: VolatilityForm,
                props: (route) => ({
                  accountId: route.params.id,
                  volatilityId: route.params.volatilityId,
                }),
              },
              {
                path: 'stockdrop/:stockdropId',
                meta: {
                  allowedUserTypes: [UserRole.Admin, UserRole.Broker, UserRole.Client],
                  categoryType: 'FLP',
                },
                name: 'account-analytics-category-stockdrop',
                component: StockDropPage,
                props: true,
              },
              {
                path: 'stockdrop/new',
                meta: {
                  title: 'New Stock Drop Model',
                  allowedUserTypes: [UserRole.Admin, UserRole.Broker],
                },
                name: 'account-analytics-category-stockdrop-new',
                component: StockDropForm,
              },
              {
                path: 'stockdrop/:stockdropId/edit',
                meta: {
                  title: 'Edit Stock Drop Model',
                  allowedUserTypes: [UserRole.Admin, UserRole.Broker],
                },
                name: 'account-analytics-category-stockdrop-edit',
                component: StockDropForm,
              },
              {
                path: 'cyber/view',
                meta: {
                  title: 'Cyber Model',
                  allowedUserTypes: [UserRole.Admin, UserRole.Broker, UserRole.Client],
                },
                name: 'account-analytics-category-cyber',
                component: AccountCybeta,
                props: true,
              },
              {
                path: 'cyber/new',
                meta: {
                  title: 'New Cyber Model',
                  allowedUserTypes: [UserRole.Admin, UserRole.Broker],
                },
                name: 'account-analytics-category-cyber-new',
                component: CyberForm,
                props: { isEditMode: false },
              },
              {
                path: 'cyber/edit',
                meta: {
                  title: 'Edit Cyber Model',
                  allowedUserTypes: [UserRole.Admin, UserRole.Broker],
                },
                name: 'account-analytics-category-cyber-edit',
                component: CyberForm,
                props: { isEditMode: true },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/parent/account/:id',
    component: ParentAccountView,
    meta: {
      protectAccount: true,
    },
    children: [
      {
        path: 'overview',
        component: ParentAccountOverview,
        meta: {
          title: 'Overview | Account',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          isParentAccount: true,
          routeGroup: 'Parent Account',
          protectAccount: true,
          requiredPermissions: { [UserRole.Client]: [UserPermission.OverviewRead] },
        },
      },
      {
        path: 'policy-detail',
        name: 'parent-account-policy-detail',
        component: PolicyDetails,
        meta: {
          title: 'Policies | Account',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          isParentAccount: true,
          routeGroup: 'Parent Account',
          protectAccount: true,
        },
        props: { scenario: 'parentAccount' },
      },
      {
        path: 'card-view',
        component: ParentAccountCardView,
        meta: {
          title: 'Card View | Account',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          isParentAccount: true,
          routeGroup: 'Parent Account',
          protectAccount: true,
          requiredPermissions: { [UserRole.Client]: [UserPermission.CardviewRead] },
        },
      },
      {
        path: 'claim-detail',
        component: AccountClaims,
        meta: {
          title: 'Claims | Account',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          isParentAccount: true,
          routeGroup: 'Parent Account',
          protectAccount: true,
          requiredPermissions: [UserPermission.ClaimsRead],
        },
        props: { showParentAccount: true },
      },
      {
        path: 'documents',
        component: DocumentList,
        meta: {
          title: 'Documents | Account',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
          isParentAccount: true,
          routeGroup: 'Parent Account',
          protectAccount: true,
        },
        props: { scenario: 'parentAccount' },
      },
    ],
  },

  {
    path: '/management-analytics',
    name: 'ManagementAnalytics',
    meta: {
      title: 'Management Reporting',
      routeGroup: 'Management Reporting',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
    },
    components: { default: ManagementAnalyticsDashboard },
  },
  {
    path: '/revenue-dashboard',
    name: 'RevenueDashboard',
    meta: {
      title: 'Revenue Dashboard',
      routeGroup: 'Management Reporting',
      allowedUserTypes: [UserRole.Admin],
    },
    components: { default: RevenueDashboard },
  },
  {
    path: '/yield-analysis',
    name: 'YieldAnalysis',
    meta: {
      title: 'Yield Analysis',
      routeGroup: 'Management Reporting',
      allowedUserTypes: [UserRole.Admin],
    },
    components: { default: YieldAnalysis },
  },
  {
    path: '/logout',
    name: 'Logout',
    meta: { skipRouteGroup: true, title: 'Logout' },
    component: Logout,
  },

  // needed for Amplify
  {
    path: '/signout',
    meta: { skipRouteGroup: true },
    redirect: '/',
  },

  {
    path: '/unauthorized',
    name: 'Unauthorized',
    meta: { skipRouteGroup: true, title: 'Unauthorized' },
    component: Unauthorized,
  },

  {
    path: '/not-found',
    name: 'Not Found',
    meta: {
      skipRouteGroup: true,
      title: 'Not Found',
      routeGroup: 'Account',
    },
    component: NotFound,
  },

  {
    path: '/carrier/dashboard',
    name: 'CarrierOverviewDashboard',
    meta: {
      title: 'Dashboard',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
    },
    component: CarrierOverviewDashboard,
  },

  {
    path: '/directory',
    name: 'LandingPage',
    meta: {
      skipRouteGroup: true,
      title: 'Platform Directory',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
    },
    component: LandingPage,
  },

  {
    path: '/home',
    name: 'Home',
    meta: {
      skipRouteGroup: true,
      title: 'Home',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
    },
    component: Home,
    alias: '/',
  },

  {
    path: '/accounts',
    name: 'AccountList',
    meta: {
      title: 'Accounts',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
      routeGroup: 'Account',
    },
    component: AccountList,
  },

  {
    path: '/clients',
    name: 'ClientView',
    meta: {
      title: 'Clients',
      routeGroup: 'Account',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
    },
    component: ClientView,
  },

  {
    path: '/am-best',
    name: 'AMBestRatings',
    meta: {
      title: 'AM Best Ratings',
      routeGroup: 'Carrier',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
    },
    component: AMBestRatings,
  },

  {
    path: '/carriers',
    name: 'CarrierList',
    meta: {
      routeGroup: 'Carrier',
      title: 'Carriers',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Market],
    },
    components: { default: CarrierList },
  },

  {
    path: '/carriers/surveys',
    component: CarrierSurveyList,
    meta: {
      routeGroup: 'Carrier',
      title: 'Carrier Survey List',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
    },
  },
  {
    path: '/carriers/survey/do',
    component: CarrierSurvey,
    props: { scenario: 'do' },
    meta: {
      routeGroup: 'Carrier',
      title: 'Carrier Survey',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
    },
  },
  {
    path: '/carriers/survey/tl',
    component: CarrierSurvey,
    props: { scenario: 'tl' },
    meta: {
      routeGroup: 'Carrier',
      title: 'Carrier Survey',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
    },
  },
  {
    path: '/carriers/survey/cyber',
    component: CarrierSurvey,
    props: { scenario: 'cyber' },
    meta: {
      routeGroup: 'Carrier',
      title: 'Cyber Survey',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin],
    },
  },

  {
    path: '/carrier/:id',
    component: CarrierView,
    children: [
      {
        path: 'overview',
        component: CarrierOverview,
        meta: {
          routeGroup: 'Carrier',
          title: 'Carrier',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Market],
        },
      },
      {
        path: 'policy-detail',
        name: 'carrier-policy-detail',
        component: PolicyList,
        meta: {
          routeGroup: 'Carrier',
          title: 'Carrier',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Market],
        },
        props: { scenario: 'carrier' },
      },
      {
        path: 'insurer-ratings',
        component: CarrierInsurerFinancials,
        meta: {
          routeGroup: 'Carrier',
          title: 'Carrier',
          allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Market],
        },
      },
    ],
  },

  {
    path: '/admin/cache',
    meta: {
      routeGroup: 'Admin',
      title: 'Cache Management',
      allowedUserTypes: [UserRole.Admin],
    },
    component: CacheManagement,
  },

  {
    path: '/admin/carrier-issuer',
    name: 'CarrierGroupMapping',
    meta: {
      routeGroup: 'Admin',
      title: 'Carrier / Issuer Association',
      allowedUserTypes: [UserRole.Admin],
    },
    components: { default: CarrierGroupMapping },
  },
  {
    path: '/admin/policy-type',
    name: 'PolicyType',
    meta: {
      routeGroup: 'Admin',
      title: 'Policy Type / Coverage Type Maintenance',
      allowedUserTypes: [UserRole.Admin],
    },
    components: { default: PolicyType },
  },
  {
    path: '/admin/wholesaler',
    name: 'Wholesaler',
    meta: {
      routeGroup: 'Admin',
      title: 'Wholesaler / Group Maintenance',
      allowedUserTypes: [UserRole.Admin],
    },
    components: { default: Wholesaler },
  },
  {
    path: '/admin/permissions',
    name: 'PermissionAdmin',
    meta: {
      routeGroup: 'Admin',
      title: 'Permission Maintenance',
      allowedUserTypes: [UserRole.Admin],
    },
    components: { default: PermissionAdmin },
  },
  {
    path: '/admin/component-showcase',
    name: 'CustomComponents',
    meta: {
      routeGroup: 'Admin',
      title: 'Custom Components',
      allowedUserTypes: [UserRole.Admin],
      skipRouteGroup: import.meta.env.MODE === 'development',
    },
    component: ComponentShowcase,
  },
  {
    path: '/admin/ai',
    name: 'PromptManager',
    meta: {
      routeGroup: 'Admin',
      title: 'Prompt Manager',
      allowedUserTypes: [UserRole.Admin],
    },
    component: PromptManager,
  },
  {
    path: '/ai-results',
    name: 'AIVerificationResults',
    meta: {
      routeGroup: 'Account',
      title: 'AI Data Extraction Results',
      allowedUserTypes: [UserRole.Admin, UserRole.Broker],
      requiredPermissions: { [UserRole.Broker]: [UserPermission.AIVerificationWrite] },
    },
    component: AIVerificationResultsPage,
  },
  {
    path: '/admin/reporting',
    name: 'AdminReporting',
    meta: {
      routeGroup: 'Admin',
      title: 'Reporting',
      allowedUserTypes: [UserRole.Admin],
    },
    component: Reporting,
  },
  {
    path: '/analytics',
    name: 'AnalyticsHub',
    components: { default: AnalyticsHub },
    meta: {
      routeGroup: 'Analytics',
      title: 'Analytics Hub',
      allowedUserTypes: [UserRole.Admin, UserRole.Broker],
    },
    children: [
      {
        path: 'category/:categoryId',
        name: 'analytics-category',
        component: AnalyticsList,
        children: [
          {
            path: 'dashboard/:dashboardId',
            name: 'analytics-category-dashboard',
            component: AnalyticsView,
            props: true,
          },
          {
            path: 'volatility/:volatilityId',
            meta: {
              allowedUserTypes: [UserRole.Admin, UserRole.Broker],
              categoryType: 'Other',
            },
            name: 'analytics-category-volatility',
            component: VolatilityShow,
            props: true,
          },
          {
            path: 'volatility/new',
            meta: {
              title: 'New Volatility Model',
              allowedUserTypes: [UserRole.Admin, UserRole.Broker],
            },
            name: 'analytics-category-volatility-new',
            component: VolatilityForm,
          },
          {
            path: 'volatility/:volatilityId/edit',
            meta: {
              title: 'Edit Volatility Model',
              allowedUserTypes: [UserRole.Admin, UserRole.Broker],
            },
            name: 'analytics-category-volatility-edit',
            component: VolatilityForm,
          },
          {
            path: 'stockdrop/:stockdropId',
            meta: {
              allowedUserTypes: [UserRole.Admin, UserRole.Broker],
              categoryType: 'FLP',
            },
            name: 'analytics-category-stockdrop',
            component: StockDropPage,
          },
          {
            path: 'stockdrop/new',
            meta: {
              title: 'New Stock Drop Model',
              allowedUserTypes: [UserRole.Admin, UserRole.Broker],
            },
            name: 'analytics-category-stockdrop-new',
            component: StockDropForm,
          },
          {
            path: 'stockdrop/:stockdropId/edit',
            meta: {
              title: 'Edit Stock Drop Model',
              allowedUserTypes: [UserRole.Admin, UserRole.Broker],
            },
            name: 'analytics-category-stockdrop-edit',
            component: StockDropForm,
          },
          {
            path: 'cyber/:id/view', // id is the accountId for the account associated with the cyber model
            meta: {
              title: 'Cyber Model',
              allowedUserTypes: [UserRole.Admin, UserRole.Broker, UserRole.Client],
            },
            name: 'analytics-category-cyber',
            component: AccountCybeta,
            props: true,
          },
          {
            path: 'cyber/new',
            meta: {
              title: 'New Cyber Model',
              allowedUserTypes: [UserRole.Admin, UserRole.Broker],
            },
            name: 'analytics-category-cyber-new',
            component: CyberForm,
            props: { isEditMode: false },
          },
          {
            path: 'cyber/:id/edit', // id is the accountId for the account associated with the cyber model
            meta: {
              title: 'Edit Cyber Model',
              allowedUserTypes: [UserRole.Admin, UserRole.Broker],
            },
            name: 'analytics-category-cyber-edit',
            component: CyberForm,
            props: { isEditMode: true },
          },
        ],
      },
    ],
  },

  {
    path: '/volatility/new',
    meta: {
      title: 'New Volatility Model',
      allowedUserTypes: [UserRole.Admin, UserRole.Broker],
    },
    component: VolatilityForm,
  },

  {
    path: '/volatility/:volatilityId/edit',
    meta: {
      title: 'Edit Volatility Model',
      allowedUserTypes: [UserRole.Admin, UserRole.Broker],
    },
    component: VolatilityForm,
  },

  {
    path: '/volatility/:volatilityId/show',
    meta: {
      title: 'Volatility Model',
      allowedUserTypes: [UserRole.Admin, UserRole.Broker],
    },
    component: VolatilityShow,
  },

  {
    path: '/policy/explorer',
    meta: {
      routeGroup: 'Account',
      title: 'Policy Explorer',
      allowedUserTypes: [UserRole.Admin, UserRole.Broker],
    },
    props: { scenario: 'all' },
    component: PolicyList,
  },
  {
    path: '/stockdrop/new',
    meta: {
      allowedUserTypes: [UserRole.Admin, UserRole.Broker],
    },
    component: StockDropForm,
  },
  {
    path: '/admin/data-quality',
    meta: {
      routeGroup: 'Admin',
      title: 'Data Quality',
      allowedUserTypes: [UserRole.Admin],
    },
    component: DataQuality,
  },

  {
    path: '/profile',
    component: ManageProfile,
    meta: {
      title: 'Manage Profile',
      allowedUserTypes: [UserRole.Broker, UserRole.Client, UserRole.Admin, UserRole.Market],
      routeGroup: 'Profile',
    },
  },

  {
    path: '/notifications',
    component: NotificationPage,
    meta: {
      routeGroup: 'Notifications',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
      requiredPermissions: [UserPermission.NotificationsRead],
    },
  },

  {
    path: '/notifications/preferences',
    component: NotificationPreferences,
    meta: {
      routeGroup: 'Notifications',
      allowedUserTypes: [UserRole.Broker, UserRole.Admin, UserRole.Client],
      requiredPermissions: [UserPermission.NotificationsRead],
    },
  },

  {
    path: '/playground',
    component: Playground,
    meta: {
      title: 'UX',
      allowedUserTypes: [UserRole.Admin],
    },
    beforeEnter: (to, from, next) => {
      if (import.meta.env.DEV) {
        next(); // Proceed if the route should be enabled
      } else {
        next(false); // Cancel the navigation if the route should be disabled
      }
    },
  },

  {
    path: '/reporting',
    name: 'ReportingHub',
    components: { default: ReportingHub },
    meta: {
      routeGroup: 'Management Reporting',
      title: 'Reporting Hub',
      allowedUserTypes: [UserRole.Admin, UserRole.Broker],
    },
    children: [
      {
        path: 'category/:categoryId',
        name: 'reporting-category',
        component: ReportingList,
        props: true,
        children: [
          {
            path: 'dashboard/:dashboardId',
            name: 'reporting-category-dashboard',
            component: AnalyticsView,
            props: true,
          },
        ],
      },
    ],
  },

  {
    path: '/(.*)*',
    meta: { skipRouteGroup: true },
    component: NotFound,
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

const DEFAULT_TITLE = 'CAC Platform';

router.beforeResolve((to) => {
  const user = store.state.user;
  if (!user) {
    return { name: 'Unauthorized' };
  }

  if (to?.meta?.skipRouteGroup !== true) {
    let allowedPage = AreAllRouteGroupsAllowed(user)
      ? true
      : (user.allowedRouteGroups || []).includes(to?.meta?.routeGroup ?? '');

    if (to?.meta?.protectAccount) {
      const params = to?.params;

      if (params.id && typeof params.id === 'string') {
        allowedPage =
          AreAllRouteGroupsAllowed(user) || AreAllAccountsAllowed(user)
            ? true
            : (GetAllowedAccountIds(user) || []).includes(parseInt(params.id));
      }
    }

    if (!allowedPage) {
      return { name: 'Home' };
    }
  }

  const authPaths = to.matched.filter(isAuthRequiredOnPath);
  if (authPaths.length > 0) {
    if (!authPaths.every((record) => doesUserHaveRequiredAuthForPath(record, user))) {
      return { name: 'Unauthorized' };
    }
  }
});

function isAuthRequiredOnPath(record: RouteRecord): boolean {
  const meta = record.meta as RouteMetadata;
  return (meta?.allowedUserTypes?.length || 0) > 0;
}

export function doesUserHaveRequiredAuthForPath(record: RouteRecord, user: User): boolean {
  const meta = record.meta as RouteMetadata;
  const { allowedUserTypes, requiredPermissions } = meta;
  const userType = GetUserRole(user);

  if (IsUserAnAdmin(user)) {
    return true;
  }

  if (allowedUserTypes && allowedUserTypes?.length > 0) {
    if (!allowedUserTypes.includes(userType)) {
      return false;
    }
  }

  // requiredPermissions is an array, this will check against all user types
  if (requiredPermissions) {
    if (Array.isArray(requiredPermissions) && requiredPermissions.length > 0) {
      if (IsUserABroker(user)) {
        for (const permission of requiredPermissions) {
          // if user is a broker and the permission is in the brokerRequiredPermissions list, check if the user has the permission
          if (BrokerRequiredPermissions.has(permission) && !UserHasPermission(user, permission)) {
            return false;
          }
        }
      } else {
        for (const permission of requiredPermissions) {
          if (!UserHasPermission(user, permission)) {
            return false;
          }
        }
      }
    } else if (typeof requiredPermissions === 'object') {
      if (requiredPermissions[userType] && requiredPermissions[userType].length > 0) {
        for (const permission of requiredPermissions[userType]) {
          if (!UserHasPermission(user, permission)) {
            return false;
          }
        }
      }
    }
  }

  return true;
}

router.afterEach((to: RouteLocationNormalized, from, failure) => {
  // Use next tick to handle router history correctly
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  nextTick(() => {
    updateTitle(to);
  });

  // used to update users last active date in the db
  Commit(StoreMutationType.SET_LAST_ACTIVE_DATE, new Date());

  // Reset error state after the route has been finalized.
  if (!failure) {
    Commit(StoreMutationType.SET_HAS_ERROR, false);
  }
});

function updateTitle(to: RouteLocationNormalized): void {
  const { title } = to.meta as Record<string, unknown>;
  document.title = title && typeof title === 'string' ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
}

export default router;
